/**
 * AnnouncementBar.jsx  —  FIXED
 *
 * ROOT CAUSE FIXED (Part 1 — Layout Shift):
 *   The original component did `return null` on dismiss. This removed the element
 *   from the DOM, which triggered the useEffect cleanup, which called
 *   `document.body.removeAttribute('data-announcement')`. That removal instantly
 *   wiped the CSS rule that was keeping the navbar offset + body padding-top in
 *   sync, causing a hard layout jump that visually displaced every fixed element
 *   including the BottomNav.
 *
 * FIX STRATEGY:
 *   1. Never remove the component from the DOM while messages exist.
 *   2. Hide via CSS transform (slide up off-screen) + aria-hidden, NOT `return null`.
 *   3. Manage `data-announcement` attribute solely from the `visible` state so CSS
 *      transitions on body padding-top can run before the attribute is removed.
 *   4. Uses sessionStorage — dismissed for the current browser session only,
 *      and page reloads (matching the requirement spec).
 *
 * UNCHANGED: message rotation, prev/next arrows, bgColor/textColor props.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

// sessionStorage: dismissed for current session only — bar reappears on every new visit.
// Do NOT use localStorage here; that would permanently suppress a promotional banner
// after a single dismiss, which is not the intended UX.
const STORAGE_KEY = 'announcement_dismissed';

export default function AnnouncementBar({
  messages  = [],
  bgColor   = '#1A1A1A',
  textColor = '#FFFFFF',
}) {
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState(0);
  const [paused,  setPaused]  = useState(false);

  // Tracks whether we're mid-dismiss transition so we can defer aria-hidden
  const dismissTimer = useRef(null);

  // ── Initialise from sessionStorage ───────────────────────────────────────
  useEffect(() => {
    if (!messages.length) return;

    const dismissed = sessionStorage.getItem(STORAGE_KEY);
    if (!dismissed) {
      setVisible(true);
      document.body.setAttribute('data-announcement', 'true');
    }
    // No cleanup removal here — we manage the attribute in the visibility effect below
  }, [messages.length]);

  // ── Keep body attribute in sync with visibility state ─────────────────────
  // Runs AFTER the CSS transition has been committed to the browser, so the
  // layout change (padding-top reduction) happens after the bar has slid away.
  useEffect(() => {
    if (visible) {
      document.body.setAttribute('data-announcement', 'true');
    } else {
      // Allow the CSS slide-up transition (0.3 s) to finish before removing the
      // padding-top on body — prevents the content jump.
      dismissTimer.current = setTimeout(() => {
        document.body.removeAttribute('data-announcement');
      }, 300); // matches transition duration in index.css
    }

    return () => {
      clearTimeout(dismissTimer.current);
    };
  }, [visible]);

  // Ensure attribute is cleaned up if the component is ever fully unmounted
  // (e.g. navigating to an admin page that uses its own layout)
  useEffect(() => {
    return () => {
      clearTimeout(dismissTimer.current);
      document.body.removeAttribute('data-announcement');
    };
  }, []);

  // ── Auto-rotate messages ───────────────────────────────────────────────────
  useEffect(() => {
    if (!visible || paused || messages.length <= 1) return;
    const id = setInterval(() => {
      setCurrent(i => (i + 1) % messages.length);
    }, 4000);
    return () => clearInterval(id);
  }, [visible, paused, messages.length]);

  // ── Dismiss ────────────────────────────────────────────────────────────────
  const dismiss = useCallback(() => {
    setVisible(false);
    sessionStorage.setItem(STORAGE_KEY, '1');
  }, []);

  const prev = () => setCurrent(i => (i - 1 + messages.length) % messages.length);
  const next = () => setCurrent(i => (i + 1) % messages.length);

  // Don't render at all if there are no messages to show (saves DOM nodes)
  if (!messages.length) return null;

  return (
    <div
      className={`announcement-bar${visible ? '' : ' announcement-bar--hidden'}`}
      style={{ background: bgColor, color: textColor }}
      aria-hidden={!visible}
      aria-live="polite"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Prev arrow */}
      {messages.length > 1 && (
        <button
          onClick={prev}
          aria-label="Previous announcement"
          tabIndex={visible ? 0 : -1}
          className="absolute left-3 opacity-60 hover:opacity-100 transition-opacity p-1"
        >
          <ChevronLeft size={14} />
        </button>
      )}

      {/* Message */}
      <p className="text-center px-8 sm:px-4 font-medium tracking-wide text-xs sm:text-sm">
        {messages[current]}
      </p>

      {/* Next arrow */}
      {messages.length > 1 && (
        <button
          onClick={next}
          aria-label="Next announcement"
          tabIndex={visible ? 0 : -1}
          className="absolute right-10 opacity-60 hover:opacity-100 transition-opacity p-1"
        >
          <ChevronRight size={14} />
        </button>
      )}

      {/* Dismiss */}
      <button
        onClick={dismiss}
        aria-label="Close announcement"
        tabIndex={visible ? 0 : -1}
        className="absolute right-3 opacity-60 hover:opacity-100 transition-opacity p-1"
      >
        <X size={14} />
      </button>
    </div>
  );
}